import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Warehouse, AlertTriangle, Package, Plus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInventory = () => {
  const { getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [products, setProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustData, setAdjustData] = useState({ product_id: "", quantity_change: 0, reason: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [prodRes, lowRes, expRes, logsRes] = await Promise.all([
        axios.get(`${API}/products/all`, { headers }),
        axios.get(`${API}/inventory/low-stock`, { headers }),
        axios.get(`${API}/inventory/expiring?days=60`, { headers }),
        axios.get(`${API}/inventory/logs`, { headers }),
      ]);
      setProducts(prodRes.data);
      setLowStock(lowRes.data);
      setExpiring(expRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error(t("failedToFetchInventory"));
    } finally {
      setLoading(false);
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory/adjust`, adjustData, { headers: getAuthHeaders() });
      toast.success(t("stockAdjusted"));
      setDialogOpen(false);
      setAdjustData({ product_id: "", quantity_change: 0, reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToAdjustStock"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading font-semibold text-white">{t("inventoryManagement")}</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#d4af37] text-[#0f172a]" data-testid="adjust-stock-btn">
              <Plus className="mr-2 h-4 w-4" /> {t("adjustStock")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("adjustStock")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdjust} className="space-y-4">
              <div>
                <Label className="text-slate-300">{t("product")}</Label>
                <select
                  value={adjustData.product_id}
                  onChange={(e) => setAdjustData({ ...adjustData, product_id: e.target.value })}
                  required
                  className="w-full h-10 rounded-md bg-slate-700 border border-slate-600 px-3 text-white"
                >
                  <option value="">{t("selectProduct")}</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({t("stock")}: {p.stock_quantity})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-slate-300">{t("quantityChange")} (+ / -)</Label>
                <Input
                  type="number"
                  value={adjustData.quantity_change}
                  onChange={(e) => setAdjustData({ ...adjustData, quantity_change: parseInt(e.target.value) || 0 })}
                  required
                  placeholder={t("quantityChangePlaceholder")}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-300">{t("reason")}</Label>
                <Input
                  value={adjustData.reason}
                  onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                  required
                  placeholder={t("reasonPlaceholder")}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">{t("cancel")}</Button>
                <Button type="submit" className="bg-[#d4af37] text-[#0f172a]">{t("adjust")}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-red-900/20 border-red-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-white">{lowStock.length}</p>
                <p className="text-sm text-red-400">{t("lowStockItems")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-yellow-900/20 border-yellow-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Package className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-white">{expiring.length}</p>
                <p className="text-sm text-yellow-400">{t("expiringDays")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Warehouse className="w-8 h-8 text-slate-400" />
              <div>
                <p className="text-2xl font-bold text-white">{products.length}</p>
                <p className="text-sm text-slate-400">{t("totalProducts")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="low-stock" className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="low-stock" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f172a]">{t("lowStock")}</TabsTrigger>
          <TabsTrigger value="expiring" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f172a]">{t("expiringSoon")}</TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f172a]">{t("activityLogs")}</TabsTrigger>
        </TabsList>

        <TabsContent value="low-stock">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-0">
              {lowStock.length === 0 ? (
                <div className="p-8 text-center text-slate-400">{t("noLowStock")}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-300">{t("product")}</TableHead>
                      <TableHead className="text-slate-300">{t("sku")}</TableHead>
                      <TableHead className="text-slate-300">{t("currentStock")}</TableHead>
                      <TableHead className="text-slate-300">{t("minAlert")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lowStock.map((p) => (
                      <TableRow key={p.id} className="border-slate-700">
                        <TableCell className="text-white font-medium">{p.name}</TableCell>
                        <TableCell className="text-slate-300">{p.sku || "-"}</TableCell>
                        <TableCell><Badge className="bg-red-500/20 text-red-400">{p.stock_quantity}</Badge></TableCell>
                        <TableCell className="text-slate-400">{p.min_stock_alert}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expiring">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-0">
              {expiring.length === 0 ? (
                <div className="p-8 text-center text-slate-400">{t("noExpiring")}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-300">{t("product")}</TableHead>
                      <TableHead className="text-slate-300">{t("batch")}</TableHead>
                      <TableHead className="text-slate-300">{t("expiryDate")}</TableHead>
                      <TableHead className="text-slate-300">{t("stock")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiring.map((p) => (
                      <TableRow key={p.id} className="border-slate-700">
                        <TableCell className="text-white font-medium">{p.name}</TableCell>
                        <TableCell className="text-slate-300">{p.batch_number || "-"}</TableCell>
                        <TableCell><Badge className="bg-yellow-500/20 text-yellow-400">{p.expiry_date}</Badge></TableCell>
                        <TableCell className="text-slate-300">{p.stock_quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-0">
              {logs.length === 0 ? (
                <div className="p-8 text-center text-slate-400">{t("noInventoryLogs")}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-300">{t("date")}</TableHead>
                      <TableHead className="text-slate-300">{t("product")}</TableHead>
                      <TableHead className="text-slate-300">{t("change")}</TableHead>
                      <TableHead className="text-slate-300">{t("reason")}</TableHead>
                      <TableHead className="text-slate-300">{t("by")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.slice(0, 20).map((log) => (
                      <TableRow key={log.id} className="border-slate-700">
                        <TableCell className="text-slate-400">{new Date(log.created_at).toLocaleDateString(isRTL ? 'ar-LY' : 'en-US')}</TableCell>
                        <TableCell className="text-white">{log.product_name}</TableCell>
                        <TableCell>
                          <Badge className={log.quantity_change > 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                            {log.quantity_change > 0 ? "+" : ""}{log.quantity_change}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{log.reason}</TableCell>
                        <TableCell className="text-slate-400">{log.created_by}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInventory;
